import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Home, BarChart3, UserCog, FileText, Bell, ChevronDown, Users, TrendingUp, Award, Search, Mail, Download, MessageCircle, Calendar, Clock, ArrowRight, Zap, FileBarChart, Send, HelpCircle, CheckCircle, AlertCircle, Activity, Filter, ExternalLink, Building2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { AIInsightPopover } from '../components/AIInsightPopover';
export function AdminHub() {
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCourse, setSelectedCourse] = useState('');
  const [reminderLearner, setReminderLearner] = useState('');
  const [isReminderDropdownOpen, setIsReminderDropdownOpen] = useState(false);
  const [timePeriod, setTimePeriod] = useState<'7d' | '30d'>('30d');
  const userMenuRef = useRef<HTMLDivElement>(null);
  const reminderDropdownRef = useRef<HTMLDivElement>(null);
  const incompleteLearners = [{
    value: 'rachel-patel',
    name: 'Rachel Patel',
    email: 'rachel.patel@example.com'
  }, {
    value: 'david-nguyen',
    name: 'David Nguyen',
    email: 'd.nguyen@example.com'
  }, {
    value: 'karen-brooks',
    name: 'Karen Brooks',
    email: 'k.brooks@example.com'
  }, {
    value: 'marcus-hall',
    name: 'Marcus Hall',
    email: 'marcus.h@example.com'
  }, {
    value: 'tanya-foster',
    name: 'Tanya Foster',
    email: 'tanya.f@example.com'
  }];
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
      if (reminderDropdownRef.current && !reminderDropdownRef.current.contains(event.target as Node)) {
        setIsReminderDropdownOpen(false);
      }
    };
    if (isUserMenuOpen || isReminderDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUserMenuOpen, isReminderDropdownOpen]);
  const navigationCards = [{
    title: 'Analytics Dashboard',
    description: 'View detailed performance metrics and insights',
    icon: BarChart3,
    color: 'from-[#00638C] to-[#30718D]',
    stats: '248 learners tracked',
    link: '/analytics'
  }, {
    title: 'User Management',
    description: 'Manage learner accounts and permissions',
    icon: UserCog,
    color: 'from-[#30718D] to-[#265a70]',
    stats: '18 pending invites',
    link: '/assessment'
  }, {
    title: 'Reports',
    description: 'Generate and download custom reports',
    icon: FileText,
    color: 'from-[#007558] to-[#005a44]',
    stats: 'Last report: 1 hour ago',
    link: '/feedback'
  }, {
    title: 'Insights & Actions',
    description: 'Prioritize learner support with AI-powered skill insights',
    icon: Zap,
    color: 'from-[#DB3725] to-[#DB3725]',
    stats: '4 priority insights',
    link: '/insights-actions'
  }];
  return <div className="flex-1 bg-gray-50 min-h-screen" data-id="element-1770" data-mp="2heb8ysixi3s7">
      {/* Top Navigation */}
      <div className="bg-white border-b border-gray-200" data-id="element-1771" data-mp="1kplmeowcwmzw">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1772" data-mp="1oq5irq93vmkz">
          <div className="flex items-center h-16" data-id="element-1773" data-mp="1os89kci31jqr">
            {/* Logo */}
            <div className="text-[#E53E3E] font-bold text-xl" data-id="element-1774" data-mp="1togsm0zehx37">LOGO</div>

            {/* Navigation Links */}
            <nav className="flex items-center gap-4 sm:gap-8 ml-6 sm:ml-12" data-id="element-1775" data-mp="1ypwe1u5hq5m4">
              <Link to="/admin" className="flex items-center gap-2 text-[#00638C] border-b-2 border-[#00638C] pb-[22px] pt-[22px] transition-colors text-sm font-medium" data-id="element-1776" data-mp="353cbjz52ykpk">
                <Home size={18} data-id="element-1777" data-mp="mvknzdqmyanc" />
                <span className="hidden sm:inline" data-id="element-1778" data-mp="hvy1sestxk2e">Admin Hub</span>
              </Link>
              <Link to="/analytics" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-1779" data-mp="hyasphgkf4rl">
                <BarChart3 size={18} data-id="element-1780" data-mp="qyqveophi7ve" />
                <span className="hidden sm:inline" data-id="element-1781" data-mp="15tdgdm3mv26h">Analytics</span>
              </Link>
              <Link to="/feedback" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-1782" data-mp="10v27bdl5wvpb">
                <FileText size={18} data-id="element-1783" data-mp="1aklqkkcserkp" />
                <span className="hidden sm:inline" data-id="element-1784" data-mp="1aloj0wprd22q">Reports</span>
              </Link>
              <Link to="/assessment" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-1785" data-mp="1ojlgdvc1dcig">
                <UserCog size={18} data-id="element-1786" data-mp="236k7fp6ze4wn" />
                <span className="hidden sm:inline" data-id="element-1787" data-mp="237mwdrkqtx9x">Users</span>
              </Link>
            </nav>

            {/* Right Side - User Info */}
            <div className="flex items-center gap-2 sm:gap-4 ml-auto" data-id="element-1788" data-mp="1t8xkqgjfx924">
              <Link to="/alerts" className="relative p-2 text-gray-600 hover:text-gray-900 transition-colors" data-id="element-1789" data-mp="1tb0bx3wbx88f">
                <Bell size={20} data-id="element-1790" data-mp="1kfkn51aflj9p" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-[#DB3725] rounded-full" data-id="element-1791" data-mp="1kfavzadnh62f"></span>
              </Link>
              <div className="relative" ref={userMenuRef} data-id="element-1792" data-mp="1p95dq89p3ayv">
                <button onClick={() => setIsUserMenuOpen(!isUserMenuOpen)} className="flex items-center gap-3 hover:bg-gray-50 rounded-lg px-2 py-1 transition-colors" data-id="element-1793" data-mp="1ajqywknco1c3">
                  <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 text-sm font-medium" data-id="element-1794" data-mp="11ijcazu74oxu">
                    LT
                  </div>
                  <span className="hidden sm:inline text-sm font-medium text-gray-900" data-id="element-1795" data-mp="387l434izqg3m">
                    Linda Taylor
                  </span>
                  <ChevronDown size={16} className="text-gray-600" data-id="element-1796" data-mp="2yfxihk947ta2" />
                </button>

                {isUserMenuOpen && <div className="absolute top-full right-0 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-50" data-id="element-1797" data-mp="2ti5dppndgcog">
                    <div className="py-2" data-id="element-1798" data-mp="3r431o2kita7s">
                      <Link to="/profile" className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-1799" data-mp="3m59pcdgyzp97">
                        <Users size={18} className="text-gray-600" data-id="element-1800" data-mp="3d41t00y0kyhr" />
                        <span className="font-medium text-gray-900" data-id="element-1801" data-mp="25r9tpep5qlrr">
                          Profile
                        </span>
                      </Link>
                      <button className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-1802" data-mp="21omyd3ta2ipt">
                        <Home size={18} className="text-gray-600" data-id="element-1803" data-mp="l97kb54hnkx1" />
                        <span className="font-medium text-gray-900" data-id="element-1804" data-mp="l7ebbj6ekolv">
                          Learner Hub
                        </span>
                      </button>
                      <button className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-1805" data-mp="v029oid170mp">
                        <UserCog size={18} className="text-gray-600" data-id="element-1806" data-mp="100q298gwrpj1" />
                        <span className="font-medium text-gray-900" data-id="element-1807" data-mp="14vn5fyrib4o0">
                          Admin Hub
                        </span>
                      </button>
                      <div className="border-t border-gray-200 my-2" data-id="element-1808" data-mp="1dyelkbvbuolz"></div>
                      <button className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-1809" data-mp="3aqofz0yjpy2o">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-600" data-id="element-1810" data-mp="3fqavxi5rdsf6">
                          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" data-id="element-1811" data-mp="12itb4hpiiwo0"></path>
                          <polyline points="16 17 21 12 16 7" data-id="element-1812" data-mp="12kmmubezg72v"></polyline>
                          <line x1="21" y1="12" x2="9" y2="12" data-id="element-1813" data-mp="yisq2e7wrn7z"></line>
                        </svg>
                        <span className="font-medium text-gray-900" data-id="element-1814" data-mp="1cik94tda7tsu">
                          Logout
                        </span>
                      </button>
                    </div>
                  </div>}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-1815" data-mp="2izflc02mvfx6">
        {/* Header */}
        <div className="mb-8 flex flex-col sm:flex-row sm:items-end justify-between gap-4" data-id="element-1816" data-mp="4rmz33md8f7i">
          <div data-id="element-1817" data-mp="9m0nun6ivo37">
            <div className="text-xs font-semibold uppercase tracking-wider text-[#30718D] mb-1" data-id="element-1818" data-mp="3rtzyahao1r23">
              Administrator
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-1" data-id="element-1819" data-mp="ohy55hxolkz">
              Welcome Back, Linda
            </h1>
          </div>
          <button className="text-[#DB3725] hover:text-[#c02f1f] font-medium text-sm transition-colors self-start sm:self-auto flex items-center gap-1.5" data-id="element-1820" data-mp="ofdwklgwaf17">
            <Mail size={14} data-id="element-1821" data-mp="emez0e3f7e5g" />
            Contact support
          </button>
        </div>

        {/* Company & Send Email Section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8" data-id="element-1822" data-mp="jkgfqsddj1dr">
          {/* Company Info Card */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.05
        }} className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 flex flex-col" data-phoebe-tour="company-info" data-id="element-1823" data-mp="17a2exftqxglr">
            <div className="flex items-center gap-3 mb-4" data-id="element-1824" data-mp="h2q4djkvcawg">
              <div className="w-8 h-8 rounded-lg bg-[#30718D]/10 flex items-center justify-center flex-shrink-0" data-id="element-1825" data-mp="l0nzt458ft8a">
                <Building2 size={16} className="text-[#30718D]" data-id="element-1826" data-mp="799eurrrlhyl" />
              </div>
              <div data-id="element-1827" data-mp="c2vg7nsh9zf3">
                <div className="text-xs font-semibold uppercase tracking-wider text-gray-500" data-id="element-1828" data-mp="c3dkn7panjy3">
                  Company Name
                </div>
                <div className="text-base font-semibold text-gray-900" data-id="element-1829" data-mp="2apq6jlsds2j">
                  Lorem Ipsum
                </div>
              </div>
            </div>

            <div className="pt-4 border-t border-gray-100" data-id="element-1830" data-mp="3kjghsjis5q86">
              <div className="flex items-center gap-3" data-id="element-1831" data-mp="3pevuv98yri3j">
                <div className="w-8 h-8 rounded-lg bg-[#007558]/10 flex items-center justify-center flex-shrink-0" data-id="element-1832" data-mp="35u5conl777m7">
                  <Users size={16} className="text-[#007558]" data-id="element-1833" data-mp="30w3ue4vljcbs" />
                </div>
                <div data-id="element-1834" data-mp="3eutultdpps4d">
                  <div className="text-xs font-semibold uppercase tracking-wider text-gray-500" data-id="element-1835" data-mp="3ew4uvj7y8hqq">
                    Registered Learners
                  </div>
                  <div className="text-base font-semibold text-gray-900" data-id="element-1836" data-mp="3ot9kjt106vca">
                    4 Learners
                  </div>
                </div>
              </div>
            </div>

            <div className="pt-3" data-id="element-1837" data-mp="3jsc35inti5bu">
              <div className="flex items-center gap-3" data-id="element-1838" data-mp="2e6q6l5xv1uf">
                <div className="w-8 h-8 rounded-lg bg-[#DB3725]/10 flex items-center justify-center flex-shrink-0" data-id="element-1839" data-mp="2frfnwdebulf">
                  <Clock size={16} className="text-[#DB3725]" data-id="element-1840" data-mp="becy7rivb9m7" />
                </div>
                <div data-id="element-1841" data-mp="bc0hfhouj5aq">
                  <div className="text-xs font-semibold uppercase tracking-wider text-gray-500" data-id="element-1842" data-mp="6fjvmu54d30t">
                    Pending
                  </div>
                  <div className="text-base font-semibold text-gray-900" data-id="element-1843" data-mp="308t9j9w39dbu">
                    5 Learners
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Send Account Creation Email */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 lg:col-span-2" data-phoebe-tour="send-email" data-id="element-1844" data-mp="3dz6fj6pf6hyj">
            <div className="flex items-center gap-3 mb-4" data-id="element-1845" data-mp="smsvceu8zlq">
              <div className="w-10 h-10 rounded-lg bg-[#30718D]/10 flex items-center justify-center" data-id="element-1846" data-mp="q0k59g7oe1x">
                <Mail size={20} className="text-[#30718D]" data-id="element-1847" data-mp="1yk8yf9d7rj1n" />
              </div>
              <div data-id="element-1848" data-mp="22jrkw9koo26l">
                <h3 className="text-lg font-bold text-gray-900" data-id="element-1849" data-mp="22lazlvqoy569">
                  Send Account Creation Email
                </h3>
                <p className="text-sm text-gray-600" data-id="element-1850" data-mp="1q8qk63l75tqc">
                  Remind learners to complete account setup.{' '}
                  <span className="font-semibold text-[#DB3725]" data-id="element-1851" data-mp="1layqb40zrxji">
                    5 pending
                  </span>
                </p>
              </div>
            </div>

            <div className="flex gap-3 items-end" data-id="element-1852" data-mp="2dzcmde8isjsf">
              <div className="relative flex-1" ref={reminderDropdownRef} data-id="element-1853" data-mp="2dwqiboku8mey">
                <button onClick={() => {
                setIsReminderDropdownOpen(!isReminderDropdownOpen);
              }} className="flex items-center gap-2 w-full px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors bg-white text-sm font-medium" data-id="element-1854" data-mp="2911eh91scjcu">
                  <Users size={18} className="text-[#30718D]" data-id="element-1855" data-mp="tgmz52t30nvv" />
                  <span className="flex-1 text-left text-gray-700" data-id="element-1856" data-mp="oibncmj883b0">
                    {reminderLearner ? incompleteLearners.find(l => l.value === reminderLearner)?.name : 'All incomplete learners (5)'}
                  </span>
                  <ChevronDown size={16} className={`text-gray-400 transition-transform ${isReminderDropdownOpen ? 'rotate-180' : ''}`} data-id="element-1857" data-mp="23e34y373glcg" />
                </button>

                {isReminderDropdownOpen && <div className="absolute top-full mt-2 left-0 w-full bg-white border border-gray-200 rounded-lg shadow-lg z-10 max-h-80 overflow-y-auto" data-id="element-1858" data-mp="305n97qlap8uh">
                    <button onClick={() => {
                  setReminderLearner('');
                  setIsReminderDropdownOpen(false);
                }} className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors border-b border-gray-100 text-sm ${reminderLearner === '' ? 'bg-[#30718D]/5 text-[#30718D] font-semibold' : 'text-gray-700'}`} data-id="element-1859" data-mp="2r4z63qxfwswd">
                      All incomplete learners
                    </button>
                    {incompleteLearners.map(learner => <button key={learner.value} onClick={() => {
                  setReminderLearner(learner.value);
                  setIsReminderDropdownOpen(false);
                }} className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0 ${reminderLearner === learner.value ? 'bg-[#30718D]/5' : ''}`} data-id="element-1860" data-mp="1h54pq1fjvugs">
                        <div className={`font-medium text-sm ${reminderLearner === learner.value ? 'text-[#30718D]' : 'text-gray-900'}`} data-id="element-1861" data-mp="tftoyjy7a02n">
                          {learner.name}
                        </div>
                        <div className="text-xs text-gray-500" data-id="element-1862" data-mp="20uff4urovg00">
                          {learner.email}
                        </div>
                      </button>)}
                  </div>}
              </div>
              <button className="px-4 py-2.5 bg-[#DB3725] text-white rounded-lg hover:bg-[#c02f1f] transition-colors flex items-center gap-2 text-sm font-medium" data-id="element-1863" data-mp="2ano0qk6nedyt">
                <Send size={16} data-id="element-1864" data-mp="1hy79vqyof54p" />
                Send
              </button>
            </div>
          </motion.div>
        </div>

        {/* Performance Snapshot */}
        <div data-phoebe-tour="dashboard" className="mb-8" data-id="element-1865" data-mp="1qwsxw1mx3ccv">
          <div className="flex items-center gap-2 mb-4" data-id="element-1866" data-mp="1qznknztel99g">
            <h2 className="text-xl font-bold text-gray-900" data-id="element-1867" data-mp="1vx6y4xia1i0c">
              Performance Snapshot
            </h2>
            <AIInsightPopover align="left" headline="Participation is strong but completion is slipping" insightBeforeMetric="80% of learners were active this month, yet only" highlightedMetric="10%" insightAfterMetric=" have finished every module." explanation="The opportunity sits with active learners who have one or two modules left — a focused reminder should close the gap." insightId={12} data-id="element-1868" data-mp="32by3bdefnp06" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1869" data-mp="33m03w47dtvkv">
            {/* Card 1 */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.1
          }} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden flex flex-col" data-id="element-1870" data-mp="2ymx1krise3gl">
              <div className="p-6 flex-1 flex flex-col" data-id="element-1871" data-mp="mx4h8wr5maqv">
                <div className="mb-3 text-gray-700 leading-relaxed" data-id="element-1872" data-mp="iz6r9rpk7kmf">
                  <span className="text-4xl font-bold text-gray-900 mr-2 align-middle" data-id="element-1873" data-mp="1263lyabyu1eb">
                    55
                  </span>
                  <span className="align-middle text-sm" data-id="element-1874" data-mp="166xclbgsluaw">
                    of your invited learners have
                    <br data-id="element-1875" data-mp="sgajpzxpqymq" />
                    completed registration
                  </span>
                </div>
                <div className="flex items-center gap-1.5 text-[#007558] text-sm font-medium" data-id="element-1876" data-mp="xdsmf8l50hma">
                  <span className="text-xs" data-id="element-1877" data-mp="ikqsvry0aq4u">▲</span>
                  <span data-id="element-1878" data-mp="nhzclwysz287">16% vs 30 days ago</span>
                </div>
              </div>
              <div className="h-1.5 w-full bg-[#007558]" data-id="element-1879" data-mp="9ihir36ngyrz"></div>
            </motion.div>

            {/* Card 2 */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-visible flex flex-col" data-id="element-1880" data-mp="eg9de4yoiyzv">
              <div className="p-6 flex-1 flex flex-col" data-id="element-1881" data-mp="2e0yv4as7iqsp">
                <div className="flex items-start justify-between mb-3 gap-2" data-id="element-1882" data-mp="2rrapyhg6f3nj">
                  <div className="text-gray-700 leading-relaxed" data-id="element-1883" data-mp="2rtf3zs7shj1j">
                    <span className="text-4xl font-bold text-gray-900 mr-2 align-middle" data-id="element-1884" data-mp="2mzspdui2f533">
                      {timePeriod === '7d' ? '65%' : '80%'}
                    </span>
                    <span className="align-middle text-sm" data-id="element-1885" data-mp="2wr6uqiwp6z45">
                      of your learners have
                      <br data-id="element-1886" data-mp="3aqg717hs992u" />
                      spent time learning this{' '}
                      {timePeriod === '7d' ? 'week' : 'month'}
                    </span>
                  </div>
                  <div className="flex items-start gap-2 flex-shrink-0" data-id="element-1887" data-mp="1gbdojtr5ir9f">
                    <div className="flex items-center bg-gray-100 rounded-md p-px" data-id="element-1888" data-mp="1cak2l5x9rmj6">
                      <button onClick={() => setTimePeriod('7d')} className={`px-1.5 py-0.5 rounded text-[10px] font-semibold transition-all whitespace-nowrap leading-tight ${timePeriod === '7d' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-400 hover:text-gray-600'}`} data-id="element-1889" data-mp="1ccwr61xj8v57">
                        7 Days
                      </button>
                      <button onClick={() => setTimePeriod('30d')} className={`px-1.5 py-0.5 rounded text-[10px] font-semibold transition-all whitespace-nowrap leading-tight ${timePeriod === '30d' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-400 hover:text-gray-600'}`} data-id="element-1890" data-mp="ppumusdaix7">
                        30 Days
                      </button>
                    </div>
                  </div>
                </div>
                <div className={`flex items-center gap-1.5 text-sm font-medium ${timePeriod === '7d' ? 'text-[#007558]' : 'text-[#007558]'}`} data-id="element-1891" data-mp="1l7nrqawz99av">
                  <span className="text-xs" data-id="element-1892" data-mp="1olxdam67zc6o">▲</span>
                  <span data-id="element-1893" data-mp="1tmbogd34b28x">
                    {timePeriod === '7d' ? '12% vs prior 7 days' : '20% vs 30 days ago'}
                  </span>
                </div>
              </div>
              <div className="h-1.5 w-full bg-[#007558]" data-id="element-1894" data-mp="1jnm66j7b7fs5"></div>
            </motion.div>

            {/* Card 3 */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.3
          }} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-visible flex flex-col" data-id="element-1895" data-mp="27fb209jcamdu">
              <div className="p-6 flex-1 flex flex-col" data-id="element-1896" data-mp="2qbax3t7m661n">
                <div className="mb-3 text-gray-700 leading-relaxed" data-id="element-1897" data-mp="2v441gfz67cd7">
                  <span className="text-4xl font-bold text-gray-900 mr-2 align-middle" data-id="element-1898" data-mp="2v5f58fsm8k4b">
                    10%
                  </span>
                  <span className="align-middle text-sm" data-id="element-1899" data-mp="2zzy98xxepxqr">
                    of your learners have
                    <br data-id="element-1900" data-mp="2vy3cm6qf88jb" />
                    completed all skill modules
                  </span>
                </div>
                <div className="flex items-center gap-1.5 text-[#DB3725] text-sm font-medium" data-id="element-1901" data-mp="2qz058fmyxq86">
                  <span className="text-xs" data-id="element-1902" data-mp="2m1qgz3bbfklx">▼</span>
                  <span data-id="element-1903" data-mp="2h9yrkw3wbkxd">20% vs 30 days ago</span>
                </div>
              </div>
              <div className="h-1.5 w-full bg-[#DB3725]" data-id="element-1904" data-mp="2h85i791wemlo"></div>
            </motion.div>
          </div>
        </div>

        {/* Navigation Cards */}
        <div data-phoebe-tour="navigation-cards" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8" data-id="element-1905" data-mp="28abom5viq727">
          {navigationCards.map((card, index) => {
          const Icon = card.icon;
          return <Link key={index} to={card.link} data-id="element-1906" data-mp="1ybw78umamc5w" data-mpi={index}>
                <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.5 + index * 0.1
            }} className="group bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-lg transition-all cursor-pointer" data-id="element-1907" data-mp="dtld66z9f1pf">
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${card.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`} data-id="element-1908" data-mp="15j3voc6e0n">
                    <Icon size={24} className="text-white" data-id="element-1909" data-mp="8u8ve0n8wktp" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-[#00638C] transition-colors" data-id="element-1910" data-mp="38i0cmkj8um22">
                    {card.title}
                  </h3>
                  <p className="text-sm text-gray-600 mb-4" data-id="element-1911" data-mp="3cgpo27kq1tj2">
                    {card.description}
                  </p>
                  <div className="flex items-center justify-between" data-id="element-1912" data-mp="2ofw18z0c3tf1">
                    <span className="text-xs font-medium text-gray-500" data-id="element-1913" data-mp="2odj7zpp4lwmo">
                      {card.stats}
                    </span>
                    <ArrowRight size={16} className="text-gray-400 group-hover:text-[#00638C] group-hover:translate-x-1 transition-all" data-id="element-1914" data-mp="2yd0gsfzocqmv" />
                  </div>
                </motion.div>
              </Link>;
        })}
        </div>

        {/* Contact Support - Full Width Horizontal */}
        <motion.div data-phoebe-tour="support" initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.8
      }} className="bg-gradient-to-br from-[#30718D] to-[#265a70] rounded-xl p-6 shadow-lg text-white mb-8" data-id="element-1915" data-mp="3m0s0w5rgi7yp">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6" data-id="element-1916" data-mp="2a2itckt0zlx">
            <div className="flex items-center gap-4" data-id="element-1917" data-mp="78vmyee7be9f">
              <div className="w-12 h-12 rounded-lg bg-white/15 backdrop-blur-sm flex items-center justify-center flex-shrink-0" data-id="element-1918" data-mp="3omrcnw2f6oou">
                <HelpCircle size={24} className="text-white" data-id="element-1919" data-mp="3tkkignzt9gol" />
              </div>
              <div data-id="element-1920" data-mp="3flb3frnccsm7">
                <h3 className="text-xl font-bold mb-1" data-id="element-1921" data-mp="3foyxqt0m1j1z">Need Help?</h3>
                <p className="text-sm text-white/70 max-w-2xl" data-id="element-1922" data-mp="3jp94i7w88dhw">
                  Our support team is available 24/7 to help you with any
                  questions or issues you may have.
                </p>
              </div>
            </div>
            <div className="flex flex-col items-center md:items-end justify-center gap-3 w-full md:w-auto" data-id="element-1923" data-mp="13xbprc6bnk4h">
              <button className="w-full md:w-auto px-6 py-3 bg-white text-[#30718D] rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-2 text-sm font-semibold shadow-sm" data-id="element-1924" data-mp="18w6687gtgwlf">
                <Mail size={16} data-id="element-1925" data-mp="12fmuckwkf79w" />
                Email Support
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </div>;
}